import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api } from "./api";
import { Btn, Field, TextArea, TextInput, Toggle, useSaveState } from "./atoms";
import { MediaPicker } from "./MediaPicker";

/* ============================================================
   /admin/showcase/:slug — Case-study editor
   Variant-aware. Sectioned to mirror the live page.
============================================================ */

const VARIANTS = [
  { value: "A", label: "A · Monograph" },
  { value: "B", label: "B · Editorial" },
  { value: "C", label: "C · Lookbook" },
  { value: "BESPOKE", label: "BESPOKE · Hand-coded" },
];

const CHAPTER_LAYOUTS = [
  "split-image-left",
  "split-image-right",
  "full-bleed",
  "diptych",
  "grid-3",
  "video",
  "quote-portrait",
];

/* -----------------------------------------------------------
   Inline section card
----------------------------------------------------------- */
const SectionCard = ({ id, title, hint, children, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <section
      data-testid={`cs-section-${id}`}
      className="rounded-xl border border-white/[0.08] bg-[#0F141A]"
    >
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left"
      >
        <div>
          <div className="font-display text-[1.05rem] font-bold tracking-[-0.01em] text-white">
            {title}
          </div>
          {hint ? (
            <div className="text-xs text-neutral-500 mt-1">{hint}</div>
          ) : null}
        </div>
        <span className="text-[0.7rem] font-bold tracking-[0.3em] uppercase text-neutral-500">
          {open ? "Close" : "Open"}
        </span>
      </button>
      {open ? (
        <div className="border-t border-white/[0.06] p-5 space-y-5">
          {children}
        </div>
      ) : null}
    </section>
  );
};

/* -----------------------------------------------------------
   Title block editor — { lead, italic, accent, tail }
----------------------------------------------------------- */
const TitleBlock = ({ value = {}, onChange }) => {
  const set = (k, v) => onChange({ ...value, [k]: v });
  return (
    <div className="grid grid-cols-2 gap-3">
      <Field label="Lead">
        <TextInput value={value.lead || ""} onChange={(v) => set("lead", v)} />
      </Field>
      <Field label="Italic phrase">
        <TextInput value={value.italic || ""} onChange={(v) => set("italic", v)} />
      </Field>
      <Field label="Accent (yellow)">
        <TextInput value={value.accent || ""} onChange={(v) => set("accent", v)} />
      </Field>
      <Field label="Tail">
        <TextInput value={value.tail || ""} onChange={(v) => set("tail", v)} />
      </Field>
    </div>
  );
};

/* -----------------------------------------------------------
   Reorderable list editor
----------------------------------------------------------- */
const ListEditor = ({ items, onChange, render, addLabel = "Add item", makeNew, testIdPrefix = "list" }) => {
  const safeItems = items || [];
  const update = (i, next) => onChange(safeItems.map((it, j) => (j === i ? next : it)));
  const remove = (i) => onChange(safeItems.filter((_, j) => j !== i));
  const move = (i, dir) => {
    const arr = [...safeItems];
    const j = i + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[i], arr[j]] = [arr[j], arr[i]];
    onChange(arr);
  };
  const add = () => onChange([...safeItems, makeNew()]);
  return (
    <div className="space-y-3">
      {safeItems.map((it, i) => (
        <div
          key={i}
          data-testid={`${testIdPrefix}-${i}`}
          className="bg-[#0B0F14] border border-white/[0.06] rounded-md p-4 space-y-3"
        >
          <div className="flex items-center justify-between">
            <span className="text-[0.6rem] font-bold tracking-[0.3em] uppercase text-neutral-500">
              #{String(i + 1).padStart(2, "0")}
            </span>
            <div className="flex gap-1">
              <Btn variant="ghost" className="!px-2 !py-1" onClick={() => move(i, -1)} disabled={i === 0}>↑</Btn>
              <Btn variant="ghost" className="!px-2 !py-1" onClick={() => move(i, 1)} disabled={i === safeItems.length - 1}>↓</Btn>
              <Btn variant="danger" className="!px-2 !py-1" onClick={() => remove(i)}>×</Btn>
            </div>
          </div>
          {render(it, (next) => update(i, next))}
        </div>
      ))}
      <Btn variant="ghost" onClick={add}>＋ {addLabel}</Btn>
    </div>
  );
};

/* -----------------------------------------------------------
   Frame editor — single image-or-video tile (used twice on
   the showcase card, side by side as FRAME 01 / 02).
----------------------------------------------------------- */
const FrameEditor = ({ value = { type: "image", src: "" }, onChange, label }) => {
  const set = (k, v) => onChange({ ...value, [k]: v });
  return (
    <div className="space-y-3">
      <Field label={`${label} — type`}>
        <select
          value={value.type || "image"}
          onChange={(e) => set("type", e.target.value)}
          className="w-full bg-[#0B0F14] border border-white/[0.10] rounded-md px-3 py-2.5 text-sm text-white"
        >
          <option value="image">Image</option>
          <option value="video">Video</option>
        </select>
      </Field>
      <MediaPicker
        label={value.type === "video" ? "Video file (mp4/webm)" : "Image"}
        value={value.src || ""}
        onChange={(v) => set("src", v)}
      />
      {value.type === "video" ? (
        <MediaPicker
          label="Poster image (shown before video plays)"
          value={value.poster || ""}
          onChange={(v) => set("poster", v)}
        />
      ) : null}
    </div>
  );
};

/* -----------------------------------------------------------
   Page
----------------------------------------------------------- */
export default function ShowcaseEditPage() {
  const { slug } = useParams();
  const nav = useNavigate();
  const [cs, setCs] = useState(null);
  const { saving, saved, error, wrap } = useSaveState();

  useEffect(() => {
    api.showcaseGet(slug).then(setCs).catch(() => setCs(false));
  }, [slug]);

  if (cs === null) return <div className="text-sm text-neutral-500">Loading…</div>;
  if (cs === false) return <div className="text-sm text-red-400">Case study not found.</div>;

  const set = (k, v) => setCs({ ...cs, [k]: v });
  const setSeo = (k, v) => setCs({ ...cs, seo: { ...(cs.seo || {}), [k]: v } });
  const setNested = (parent, k, v) =>
    setCs({ ...cs, [parent]: { ...(cs[parent] || {}), [k]: v } });

  const save = () =>
    wrap(async () => {
      const slugChanged = cs.slug !== slug;
      await api.showcaseUpdate(slug, cs);
      if (slugChanged) nav(`/admin/showcase/${cs.slug}`, { replace: true });
    });

  const isBespoke = cs.variant === "BESPOKE";

  /* Frames (showcase card) — defensive defaults */
  const frames = Array.isArray(cs.frames) ? cs.frames : [];
  const setFrame = (i, v) => {
    const next = [...frames];
    next[i] = v;
    set("frames", next);
  };

  return (
    <div data-testid="admin-cs-edit" className="pb-20">
      {/* Header */}
      <div className="flex items-center justify-between gap-4 mb-9">
        <div>
          <Link
            to="/admin/showcase"
            className="text-[0.62rem] font-bold tracking-[0.3em] uppercase text-neutral-500 hover:text-[#F4D64E]"
          >
            ← Showcase
          </Link>
          <h1 className="mt-3 font-display text-3xl font-black tracking-[-0.025em] truncate">
            {cs.name || "Untitled"}
          </h1>
        </div>
        <div className="flex items-center gap-3">
          {saved ? <span className="text-[0.7rem] text-[#F4D64E]">Saved ✓</span> : null}
          {error ? <span className="text-[0.7rem] text-red-400">{error}</span> : null}
          <a
            href={`/showcase/${cs.slug}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-[0.7rem] tracking-[0.22em] uppercase font-bold text-neutral-400 hover:text-white"
          >
            View →
          </a>
          <Btn onClick={save} disabled={saving} data-testid="cs-save">
            {saving ? "Saving…" : "Save"}
          </Btn>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* ----------------- LEFT — sectioned editor ----------------- */}
        <div className="lg:col-span-2 space-y-4">
          {/* Variant + Identity */}
          <SectionCard id="meta" title="Identity & variant" defaultOpen>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Name">
                <TextInput value={cs.name || ""} onChange={(v) => set("name", v)} />
              </Field>
              <Field label="Slug" hint="URL: /showcase/{slug}">
                <TextInput value={cs.slug || ""} onChange={(v) => set("slug", v)} />
              </Field>
              <Field label="Number / Index">
                <TextInput value={cs.n || ""} onChange={(v) => set("n", v)} />
              </Field>
              <Field
                label="Variant"
                hint={
                  isBespoke
                    ? "Hand-coded detail page — only the Showcase card is editable here."
                    : "A = monograph hero, B = editorial hero, C = lookbook hero. Detail sections are identical."
                }
              >
                <select
                  value={cs.variant || "A"}
                  onChange={(e) => set("variant", e.target.value)}
                  data-testid="cs-variant"
                  className="w-full bg-[#0B0F14] border border-white/[0.10] rounded-md px-3 py-2.5 text-sm text-white"
                >
                  {VARIANTS.map((v) => (
                    <option key={v.value} value={v.value}>{v.label}</option>
                  ))}
                </select>
              </Field>
            </div>
            {isBespoke ? (
              <div className="rounded-md bg-[#1a1505] border border-[#F4D64E]/20 p-4 text-[0.85rem] text-neutral-300 leading-[1.6]">
                <strong className="text-[#F4D64E]">BESPOKE.</strong> The detail page (
                <code className="text-white">/showcase/{cs.slug}</code>) is hand-coded and not
                editable from the CMS. Only the Showcase card below — what shows up on the{" "}
                <code className="text-white">/showcase</code> listing — is editable here.
              </div>
            ) : null}
          </SectionCard>

          {/* Showcase card (always editable, including BESPOKE) */}
          <SectionCard
            id="card"
            title="Showcase card"
            hint="What displays on the /showcase listing for this project."
            defaultOpen
          >
            <div className="grid grid-cols-2 gap-3">
              <Field label="Name lead (bold)" hint="First word — e.g. &quot;Veronique&quot;.">
                <TextInput value={cs.nameLead || ""} onChange={(v) => set("nameLead", v)} />
              </Field>
              <Field label="Name tail (italic)" hint="Rest of the name — e.g. &quot;Gabai&quot;.">
                <TextInput value={cs.nameTail || ""} onChange={(v) => set("nameTail", v)} />
              </Field>
              <Field label="Industry">
                <TextInput value={cs.industry || ""} onChange={(v) => set("industry", v)} />
              </Field>
              <Field label="Year">
                <TextInput value={cs.year || ""} onChange={(v) => set("year", v)} />
              </Field>
              <Field label="Region">
                <TextInput value={cs.region || ""} onChange={(v) => set("region", v)} />
              </Field>
              <Field label="Status badge" hint="Live / Ongoing / Archived">
                <TextInput value={cs.status || ""} onChange={(v) => set("status", v)} />
              </Field>
            </div>
            <Field label="Description" hint="Paragraph shown on the showcase card.">
              <TextArea value={cs.description || ""} onChange={(v) => set("description", v)} rows={4} />
            </Field>
            <Field
              label="Services (one per line, max 5 visible on card)"
              hint="Bullets shown under the description."
            >
              <TextArea
                value={(cs.services || []).join("\n")}
                onChange={(v) => set("services", v.split(/\r?\n/).map((s) => s.trim()).filter(Boolean))}
                rows={5}
              />
            </Field>

            <Toggle
              value={cs.showCardLink !== false}
              onChange={(v) => set("showCardLink", v)}
              label='Show "Read case study ↗" link on the card'
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FrameEditor
                value={frames[0] || { type: "image", src: "" }}
                onChange={(v) => setFrame(0, v)}
                label="Frame 01"
              />
              <FrameEditor
                value={frames[1] || { type: "image", src: "" }}
                onChange={(v) => setFrame(1, v)}
                label="Frame 02"
              />
            </div>
          </SectionCard>

          {/* Hero — only for A/B/C */}
          {!isBespoke ? (
            <SectionCard
              id="hero"
              title="Hero (detail page)"
              hint={`Variant ${cs.variant} — composition adapts automatically on the live page.`}
            >
              <Field label="Hero image / cover">
                <MediaPicker
                  value={cs.heroImg || ""}
                  onChange={(v) => set("heroImg", v)}
                  label=""
                />
              </Field>
              <Field label="Tagline (used as hero subtitle on some variants)">
                <TextArea value={cs.tagline || ""} onChange={(v) => set("tagline", v)} rows={3} />
              </Field>
              <Field label="Live site URL" hint="Outbound link from the hero meta strip.">
                <TextInput value={cs.liveSite || ""} onChange={(v) => set("liveSite", v)} />
              </Field>
            </SectionCard>
          ) : null}

          {/* The rest is editable only when not BESPOKE */}
          {!isBespoke ? (
            <>
              <SectionCard id="sidecar" title="Sidecar (At a glance — right column)">
                <ListEditor
                  testIdPrefix="sidecar"
                  items={cs.sidecar}
                  onChange={(v) => set("sidecar", v)}
                  addLabel="Add row"
                  makeNew={() => ({ k: "", v: "" })}
                  render={(it, on) => (
                    <div className="grid grid-cols-2 gap-3">
                      <TextInput value={it.k || ""} onChange={(v) => on({ ...it, k: v })} placeholder="Label (e.g. Discipline)" />
                      <TextInput value={it.v || ""} onChange={(v) => on({ ...it, v: v })} placeholder="Value" />
                    </div>
                  )}
                />
              </SectionCard>

              <SectionCard id="atGlance" title="At a glance (section 01)">
                <Field label="Title">
                  <TitleBlock
                    value={(cs.atGlance && cs.atGlance.title) || {}}
                    onChange={(v) => setNested("atGlance", "title", v)}
                  />
                </Field>
                <Field label="Paragraphs (HTML allowed — one per line)">
                  <TextArea
                    rows={6}
                    value={((cs.atGlance && cs.atGlance.paras) || []).join("\n\n")}
                    onChange={(v) =>
                      setNested(
                        "atGlance",
                        "paras",
                        v.split(/\n\n+/).map((p) => p.trim()).filter(Boolean)
                      )
                    }
                  />
                </Field>
              </SectionCard>

              <SectionCard id="brief" title="The brief (section 02)">
                <Field label="Title">
                  <TitleBlock
                    value={(cs.brief && cs.brief.title) || {}}
                    onChange={(v) => setNested("brief", "title", v)}
                  />
                </Field>
                <Field label="Paragraphs (HTML allowed — separate with a blank line)">
                  <TextArea
                    rows={6}
                    value={((cs.brief && cs.brief.paras) || []).join("\n\n")}
                    onChange={(v) =>
                      setNested(
                        "brief",
                        "paras",
                        v.split(/\n\n+/).map((p) => p.trim()).filter(Boolean)
                      )
                    }
                  />
                </Field>
              </SectionCard>

              <SectionCard id="shipped" title="What we shipped (section 03)">
                <Field label="Section title">
                  <TitleBlock
                    value={cs.shippedTitle || {}}
                    onChange={(v) => set("shippedTitle", v)}
                  />
                </Field>
                <div>
                  <div className="text-[0.62rem] font-bold tracking-[0.22em] uppercase text-neutral-500 mb-3">
                    Chapters ({(cs.chapters || []).length})
                  </div>
                  <ListEditor
                    testIdPrefix="chapter"
                    items={cs.chapters}
                    onChange={(v) => set("chapters", v)}
                    addLabel="Add chapter"
                    makeNew={() => ({
                      n: String((cs.chapters?.length || 0) + 1).padStart(2, "0"),
                      label: "New",
                      layout: "split-image-left",
                      title: "",
                      img: "",
                    })}
                    render={(ch, on) => (
                      <>
                        <div className="grid grid-cols-3 gap-3">
                          <TextInput value={ch.n || ""} onChange={(v) => on({ ...ch, n: v })} placeholder="N" />
                          <TextInput value={ch.label || ""} onChange={(v) => on({ ...ch, label: v })} placeholder="Label" />
                          <select
                            value={ch.layout || "split-image-left"}
                            onChange={(e) => on({ ...ch, layout: e.target.value })}
                            className="bg-[#0B0F14] border border-white/[0.10] rounded-md px-3 py-2.5 text-sm text-white"
                          >
                            {CHAPTER_LAYOUTS.map((l) => (
                              <option key={l} value={l}>{l}</option>
                            ))}
                          </select>
                        </div>
                        <TextInput value={ch.title || ""} onChange={(v) => on({ ...ch, title: v })} placeholder="Title (lead)" />
                        <div className="grid grid-cols-2 gap-3">
                          <TextInput value={ch.titleItalic || ""} onChange={(v) => on({ ...ch, titleItalic: v })} placeholder="Title — italic line" />
                          <TextInput value={ch.titleAccent || ""} onChange={(v) => on({ ...ch, titleAccent: v })} placeholder="Title — accent (yellow)" />
                        </div>
                        {(ch.layout === "split-image-left" || ch.layout === "split-image-right" || ch.layout === "full-bleed") ? (
                          <MediaPicker label="Image" value={ch.img || ""} onChange={(v) => on({ ...ch, img: v })} />
                        ) : null}
                        {ch.layout === "diptych" ? (
                          <>
                            <div className="grid grid-cols-2 gap-3">
                              <MediaPicker label="Image A" value={ch.imgA || ""} onChange={(v) => on({ ...ch, imgA: v })} />
                              <MediaPicker label="Image B" value={ch.imgB || ""} onChange={(v) => on({ ...ch, imgB: v })} />
                            </div>
                            {/* Bottom-left captions shown over each image on the public site. */}
                            <div className="grid grid-cols-2 gap-3">
                              <TextInput
                                value={ch.captionA || ""}
                                onChange={(v) => on({ ...ch, captionA: v })}
                                placeholder="Caption A — small label over Image A"
                              />
                              <TextInput
                                value={ch.captionB || ""}
                                onChange={(v) => on({ ...ch, captionB: v })}
                                placeholder="Caption B — small label over Image B"
                              />
                            </div>
                          </>
                        ) : null}
                        {ch.layout === "grid-3" ? (
                          <div className="space-y-3">
                            {[0, 1, 2].map((idx) => (
                              <MediaPicker
                                key={idx}
                                label={`Image ${idx + 1}`}
                                value={(ch.imgs || [])[idx] || ""}
                                onChange={(v) => {
                                  const imgs = [...(ch.imgs || [])];
                                  imgs[idx] = v;
                                  on({ ...ch, imgs });
                                }}
                              />
                            ))}
                          </div>
                        ) : null}
                        {ch.layout === "video" ? (
                          <>
                            <MediaPicker label="Video file" value={ch.video || ""} onChange={(v) => on({ ...ch, video: v })} />
                            <MediaPicker label="Poster image" value={ch.poster || ""} onChange={(v) => on({ ...ch, poster: v })} />
                            <TextInput
                              value={ch.chip || ""}
                              onChange={(v) => on({ ...ch, chip: v })}
                              placeholder="Top-left chip badge (e.g. ‘Live capture’)"
                            />
                            {/* 3-column meta strip rendered under the video on the
                                public site. Each pair = label + block. Empty pairs
                                are dropped on save so the public grid stays clean. */}
                            <div className="space-y-3 pt-1">
                              <div className="text-[0.62rem] font-bold tracking-[0.22em] uppercase text-neutral-500">
                                Under-video meta (up to 3 pairs)
                              </div>
                              {[0, 1, 2].map((idx) => {
                                const col = (ch.columns || [])[idx] || { h: "", p: "" };
                                const writeCol = (next) => {
                                  const cols = [...(ch.columns || [])];
                                  // Keep array length aligned with the highest
                                  // edited slot so indexes stay stable.
                                  while (cols.length <= idx) cols.push({ h: "", p: "" });
                                  cols[idx] = next;
                                  // Strip trailing empty rows before persisting so
                                  // the public site's 1/2/3-column grid behaves.
                                  const cleaned = [...cols];
                                  while (
                                    cleaned.length > 0 &&
                                    !((cleaned[cleaned.length - 1] || {}).h || "").trim() &&
                                    !((cleaned[cleaned.length - 1] || {}).p || "").trim()
                                  ) {
                                    cleaned.pop();
                                  }
                                  on({ ...ch, columns: cleaned });
                                };
                                return (
                                  <div key={idx} className="grid grid-cols-3 gap-3">
                                    <TextInput
                                      value={col.h || ""}
                                      onChange={(v) => writeCol({ ...col, h: v })}
                                      placeholder={`Label ${idx + 1}`}
                                    />
                                    <div className="col-span-2">
                                      <TextArea
                                        rows={2}
                                        value={col.p || ""}
                                        onChange={(v) => writeCol({ ...col, p: v })}
                                        placeholder={`Block ${idx + 1}`}
                                      />
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </>
                        ) : null}
                        {ch.layout === "quote-portrait" ? (
                          <>
                            <TextArea value={ch.quoteLead || ""} onChange={(v) => on({ ...ch, quoteLead: v })} placeholder="Quote (HTML allowed)" rows={3} />
                            <TextInput
                              value={ch.quoteItalic || ""}
                              onChange={(v) => on({ ...ch, quoteItalic: v })}
                              placeholder="Quote — italic continuation (optional)"
                            />
                            <MediaPicker label="Portrait image" value={ch.portrait || ""} onChange={(v) => on({ ...ch, portrait: v })} />
                            <div className="grid grid-cols-2 gap-3">
                              <TextInput
                                value={ch.caption || ""}
                                onChange={(v) => on({ ...ch, caption: v })}
                                placeholder="Portrait caption (left)"
                              />
                              <TextInput
                                value={ch.tag || ""}
                                onChange={(v) => on({ ...ch, tag: v })}
                                placeholder="Portrait tag (right, accent color)"
                              />
                            </div>
                            <TextArea
                              value={ch.below || ""}
                              onChange={(v) => on({ ...ch, below: v })}
                              placeholder="Body copy below the quote (optional)"
                              rows={3}
                            />
                          </>
                        ) : null}
                        {/* Copy is shown for EVERY layout — diptych, grid-3,
                            video and quote-portrait all render the chapter
                            body text on the public site. */}
                        <TextArea
                          value={ch.copy || ""}
                          onChange={(v) => on({ ...ch, copy: v })}
                          placeholder="Copy"
                          rows={3}
                        />
                      </>
                    )}
                  />
                </div>
              </SectionCard>

              <SectionCard id="numbers" title="By the numbers (section 04)">
                <ListEditor
                  testIdPrefix="number"
                  items={cs.numbers}
                  onChange={(v) => set("numbers", v)}
                  addLabel="Add number"
                  makeNew={() => ({ v: "", label: "" })}
                  render={(n, on) => (
                    <div className="grid grid-cols-2 gap-3">
                      <TextInput value={n.v || ""} onChange={(v) => on({ ...n, v: v })} placeholder="Value (e.g. 5×)" />
                      <TextInput value={n.label || ""} onChange={(v) => on({ ...n, label: v })} placeholder="Label" />
                    </div>
                  )}
                />
              </SectionCard>

              <SectionCard
                id="press"
                title="In the press (optional)"
                hint="Empty = section auto-hidden on the live page."
              >
                <ListEditor
                  testIdPrefix="press"
                  items={cs.press}
                  onChange={(v) => set("press", v)}
                  addLabel="Add quote"
                  makeNew={() => ({ pub: "", quote: "" })}
                  render={(p, on) => (
                    <>
                      <TextInput value={p.pub || ""} onChange={(v) => on({ ...p, pub: v })} placeholder="Publication name" />
                      <TextArea value={p.quote || ""} onChange={(v) => on({ ...p, quote: v })} placeholder="Quote" rows={3} />
                    </>
                  )}
                />
              </SectionCard>

              <SectionCard id="outcome" title="Outcome (last section)">
                <Field label="Title">
                  <TitleBlock
                    value={(cs.outcome && cs.outcome.title) || {}}
                    onChange={(v) => setNested("outcome", "title", v)}
                  />
                </Field>
                <Field label="Paragraphs (HTML allowed — separate with a blank line)">
                  <TextArea
                    rows={6}
                    value={((cs.outcome && cs.outcome.paras) || []).join("\n\n")}
                    onChange={(v) =>
                      setNested(
                        "outcome",
                        "paras",
                        v.split(/\n\n+/).map((p) => p.trim()).filter(Boolean)
                      )
                    }
                  />
                </Field>
              </SectionCard>
            </>
          ) : null}
        </div>

        {/* ----------------- RIGHT — publish + SEO ----------------- */}
        <div className="lg:col-span-1 space-y-7">
          <Toggle
            value={!!cs.published}
            onChange={(v) => set("published", v)}
            label={cs.published ? "Live" : "Draft"}
          />

          <div className="pt-4 border-t border-white/[0.06]">
            <div className="text-[0.62rem] font-bold tracking-[0.22em] uppercase text-neutral-500 mb-4">
              SEO
            </div>
            <div className="space-y-4">
              <Field label="Meta title">
                <TextInput value={cs.seo?.title || ""} onChange={(v) => setSeo("title", v)} />
              </Field>
              <Field label="Meta description">
                <TextArea value={cs.seo?.description || ""} onChange={(v) => setSeo("description", v)} rows={3} />
              </Field>
              <Field label="OG image URL">
                <TextInput value={cs.seo?.og_image || ""} onChange={(v) => setSeo("og_image", v)} />
              </Field>
              <Field label="Canonical URL">
                <TextInput value={cs.seo?.canonical || ""} onChange={(v) => setSeo("canonical", v)} />
              </Field>
              <Toggle
                value={!!cs.seo?.noindex}
                onChange={(v) => setSeo("noindex", v)}
                label="Noindex"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
